import type { ComponentPropsWithoutRef, ReactNode, JSX } from 'react';
import { isNotNil } from '@wistia/type-guards';
import { styled, css } from 'styled-components';
import { ListItem } from './ListItem';

const spacesStyle = css`
  overflow: hidden;
  padding-left: 0;
  vertical-align: bottom;

  li {
    display: block;
    float: left;

    &::after {
      content: ' ';
      padding-right: var(--wui-space-03);
    }

    &:last-child {
      &::after {
        content: '' !important;
        padding-right: 0;
      }
    }
  }
`;

const commasStyle = css`
  ${spacesStyle};

  li {
    &::after {
      content: ',' !important;
      padding-right: var(--wui-space-01);
    }
  }
`;

const slashesStyle = css`
  ${spacesStyle};

  li {
    &::after {
      content: '/' !important;
      padding-left: var(--wui-space-01);
      padding-right: var(--wui-space-01);
    }
  }
`;

const breadcrumbsStyle = css`
  ${spacesStyle};

  li {
    &::after {
      content: '>' !important;
      padding-left: var(--wui-space-01);
      padding-right: var(--wui-space-01);
    }
  }
`;

const unbulletedStyle = css`
  list-style: none;
  padding-left: 0;
`;

type ListComponentProps = {
  $variant: string;
};

const ListComponent = styled.ul<ListComponentProps>`
  list-style-position: outside;
  margin: 0 0 var(--wui-space-01);
  padding: 0 0 0 var(--wui-space-04);
  ${({ $variant }) => $variant === 'unbulleted' && unbulletedStyle};
  ${({ $variant }) => $variant === 'spaces' && spacesStyle};
  ${({ $variant }) => $variant === 'commas' && commasStyle};
  ${({ $variant }) => $variant === 'slashes' && slashesStyle};
  ${({ $variant }) => $variant === 'breadcrumbs' && breadcrumbsStyle};

  > li:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    margin-top: var(--wui-space-01);
  }

  /* nested lists should not have bottom margins because their container <li> will have one */
  li ul,
  li ol {
    margin-bottom: 0;
  }
`;

const renderListComponent = (listItems: ReactNode, variant: VariantType, { ...otherProps }) => {
  // always use a `ul` unless we're rendering the "ordered" variant
  const elementType = variant === 'ordered' ? 'ol' : 'ul';

  return (
    <ListComponent
      $variant={variant}
      as={elementType}
      {...otherProps}
    >
      {listItems}
    </ListComponent>
  );
};

const renderListFromArray = (
  listItems: ItemType[],
  variant: VariantType,
  otherProps: Record<string, unknown>,
) => {
  let itemCount = 0;
  const items = listItems.map((listItem, i) => {
    const item = listItem;
    const nextItem = listItems[i + 1];
    const key = `item-${(itemCount += 1)}`; // hacky way to autogenerate keys

    // if the *next* item in the array is a nested list then we need to include
    // the next <ul/ol> as a child of the current <li>
    if (Array.isArray(nextItem)) {
      return (
        <ListItem key={key}>
          {item}
          {renderListFromArray(nextItem, variant, otherProps)}
        </ListItem>
      );
    }

    // if the current item is an array, do nothing because we rendered
    // these items previously
    if (Array.isArray(item)) {
      return null;
    }

    return <ListItem key={key}>{item}</ListItem>;
  });

  return renderListComponent(items, variant, otherProps);
};

export type VariantType =
  | 'breadcrumbs'
  | 'bullets'
  | 'commas'
  | 'ordered'
  | 'slashes'
  | 'spaces'
  | 'unbulleted';

type ItemType = ItemType[] | string[] | string;

export type ListProps = ComponentPropsWithoutRef<'ul'> & {
  /**
   * Children should be wrapped in `<ListItem>`
   */
  children?: ReactNode;
  /**
   * As an alternative to passing children you can supply an array of strings that
   * will be rendered as child `<ListItems>`s. Note that all items will be of the same variant.
   */
  items?: ItemType[];
  /**
   * The kind of list to render
   */
  variant?: VariantType;
};

export const List = ({
  children,
  items,
  variant,
  ...otherProps
}: ListProps): JSX.Element | null => {
  const listVariant = variant ?? 'bullets';
  if (isNotNil(children)) {
    // children exist, but it's an empty array so do nothing
    if (Array.isArray(children) && !children.length) {
      return null;
    }

    return renderListComponent(children, listVariant, otherProps);
  }

  if (isNotNil(items)) {
    // items is empty so do nothing
    if (!items.length) {
      return null;
    }

    return renderListFromArray(items, listVariant, otherProps);
  }

  // neither children nor items provided, so render nothing
  return null;
};

List.displayName = 'List';
